import { LabelStack, UFOInteractionContextType, useInteractionContext } from "@atlaskit/react-ufo/interaction-context";
import {
  addError,
  addErrorToAll,
  getActiveInteraction,
  InteractionMetrics,
} from "@atlaskit/react-ufo/interaction-metrics";
import { useCallback } from "react";

function isError(maybeError: unknown): maybeError is Error {
  return (
    maybeError !== null &&
    typeof maybeError === "object" &&
    "name" in maybeError &&
    "message" in maybeError &&
    typeof maybeError.name === "string" &&
    typeof maybeError.message === "string"
  );
}

export function unknownToString(unknown: unknown): string {
  return typeof unknown === "object" ? JSON.stringify(unknown) : String(unknown);
}

export function toError(maybeError: unknown): Error {
  return isError(maybeError) ? maybeError : new Error(unknownToString(maybeError));
}

const reportedUFOErrors: WeakSet<Error> = new WeakSet();

/**
 * Reports the current UFO segment as FAILED, if that has not already done for the given error.
 * @returns True if it was reported as FAILED, false if it had already been reported.
 */
export function reportUFOError(error: Error, origin: string, context: UFOInteractionContextType | null): boolean {
  if (reportedUFOErrors.has(error)) {
    return false;
  }

  const interaction: InteractionMetrics | undefined = getActiveInteraction();
  const labelStack: LabelStack | null = context?.labelStack ?? null;

  if (interaction?.id) {
    addError(interaction.id, origin, labelStack, error.name, error.message, error.stack);
  } else {
    addErrorToAll(origin, labelStack, error.name, error.message, error.stack);
  }

  reportedUFOErrors.add(error);
  return true;
}

export function useReportUFOError(origin: string = "[unknown]"): (error: unknown) => void {
  const ufoInteractionContext: UFOInteractionContextType | null = useInteractionContext();

  return useCallback(
    (maybeError: unknown): void => void reportUFOError(toError(maybeError), origin, ufoInteractionContext),
    [origin, ufoInteractionContext],
  );
}
